import { useEffect } from "react";

/**
 * Loads the GTM container (GTM-WVRF6HQN) after first contentful paint so the
 * tag manager bundle never competes with hydration for the main thread.
 * dataLayer is created immediately so early pushes are preserved.
 */
export function GtmContainerDeferred() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (document.getElementById("gtm-container-script")) return;

    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];

    let loaded = false;
    const load = () => {
      if (loaded) return;
      loaded = true;
      (w.dataLayer as unknown[]).push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
      });
      const s = document.createElement("script");
      s.id = "gtm-container-script";
      s.async = true;
      s.src = "https://www.googletagmanager.com/gtm.js?id=GTM-WVRF6HQN";
      document.head.appendChild(s);
    };

    const events: (keyof WindowEventMap)[] = [
      "pointerdown",
      "touchstart",
      "keydown",
      "scroll",
    ];
    const onInteract = () => {
      cleanup();
      load();
    };
    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, onInteract));
    };
    events.forEach((e) =>
      window.addEventListener(e, onInteract, { once: true, passive: true })
    );

    let timer = 0;
    // Mobile main threads are far weaker: wait for an idle window (or a much
    // longer timeout) instead of a fixed 2s tick, so GTM never lands inside
    // the LCP/TBT measurement window. Desktop keeps its previous timing.
    const isMobile =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 767px)").matches;
    const start = () => {
      if (isMobile) {
        const w2 = window as unknown as {
          requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        };
        if (typeof w2.requestIdleCallback === "function") {
          w2.requestIdleCallback(load, { timeout: 8000 });
        } else {
          timer = window.setTimeout(load, 6000);
        }
        return;
      }
      timer = window.setTimeout(load, 2000);
    };
    if (document.readyState === "complete") {
      start();
    } else {
      window.addEventListener("load", start, { once: true });
    }

    return () => {
      window.clearTimeout(timer);
      cleanup();
    };
  }, []);

  return null;
}
